export interface PaddleCheckoutSummary {
  amount: number
  currencyCode: string
  countryCode?: string
  interval: 'month' | 'year'
  trialDays: number
  discountApplied: boolean
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
}

export function paddleCheckoutDiscountApplied(data: Record<string, unknown>): boolean | undefined {
  const discount = record(data.discount)
  if (discount?.id || discount?.code) return true
  if (typeof data.discount_id === 'string' && data.discount_id.length > 0) return true
  const discounts = [data.totals, data.recurring_totals].map(totals => record(totals)?.discount)
  if (discounts.some(value => typeof value === 'number' && Number.isFinite(value) && value > 0)) return true
  return data.status === 'ready' && discounts.some(value => value === 0) ? false : undefined
}

/** Billing event amounts are major currency units, unlike PricePreview totals. */
export function getPaddleCheckoutSummary(
  data: Record<string, unknown>,
  priceId: string | null,
): PaddleCheckoutSummary | null {
  // Draft totals can be zero or omit the discount while Paddle initializes.
  if (data.status !== 'ready' || !Array.isArray(data.items) || data.items.length !== 1) return null
  const item = record(data.items[0])
  if (!item || !priceId || item.price_id !== priceId) return null
  const billing = record(item.billing_cycle)
  if (!billing || billing.frequency !== 1 || (billing.interval !== 'month' && billing.interval !== 'year')) return null
  const trial = record(item.trial_period)
  if (item.trial_period !== null && (!trial || trial.interval !== 'day' || !Number.isInteger(trial.frequency) || Number(trial.frequency) <= 0)) return null
  const trialDays = trial ? Number(trial.frequency) : 0
  const totals = record(trialDays > 0 ? data.recurring_totals : data.totals)
  const amount = totals?.balance ?? totals?.total
  if (typeof amount !== 'number' || !Number.isFinite(amount) || amount < 0) return null
  if (typeof data.currency_code !== 'string' || !/^[A-Z]{3}$/.test(data.currency_code)) return null
  const countryCode = record(record(data.customer)?.address)?.country_code
  return {
    amount,
    currencyCode: data.currency_code,
    countryCode: typeof countryCode === 'string' ? countryCode : undefined,
    interval: billing.interval,
    trialDays,
    discountApplied: paddleCheckoutDiscountApplied(data) === true,
  }
}

/**
 * Whether a completed Billing checkout item started a free trial.
 * Paddle.js exposes `trial_period` on the item itself; some payloads also
 * nest it under `item.price`. Only when neither carries trial metadata do we
 * fall back to the env price-ID mapping, using the item's actual price ID.
 * An explicit `trial_period: null` means no trial.
 */
export function paddleCheckoutItemStartsTrial(
  item: unknown,
  isTrialPriceId: (priceId: string | null | undefined) => boolean,
  fallbackPriceId?: string | null,
): boolean {
  const it = record(item)
  const price = record(it?.price)
  const source = it && 'trial_period' in it ? it : price && 'trial_period' in price ? price : null
  if (source) {
    const trial = record(source.trial_period)
    return Number(trial?.frequency ?? 0) > 0
  }
  const priceId =
    (typeof it?.price_id === 'string' && it.price_id) ||
    (typeof price?.id === 'string' && price.id) ||
    fallbackPriceId ||
    null
  return isTrialPriceId(priceId)
}
